<script setup lang="ts">
import type { Country, CustomerAddress, Salutation } from '~/types/shopware'
const { pushSuccess, pushError } = useNotifications()
const { setDefaultCustomerShippingAddress, setDefaultCustomerBillingAddress, deleteCustomerAddress } = useAddress()
const { defaultBillingAddressId, defaultShippingAddressId } = useUser()
const { refreshSessionContext } = useSessionContext()
const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    address: CustomerAddress
    countries: Country[]
    salutations: Salutation[]
    salutationLoading?: boolean
    salutationError?: boolean
    onRetrySalutations?: () => void
    canSetDefault?: boolean
    canEdit?: boolean
    canDelete?: boolean
  }>(),
  {
    canSetDefault: true,
    canEdit: true,
    canDelete: false,
    salutationLoading: false,
    salutationError: false,
    onRetrySalutations: undefined,
  },
)

const { customerAddresses } = useAddress()
const address = computed(() => customerAddresses.value.find((a) => a.id === props.address.id)!)

const canBeDeleted = computed(
  () =>
    props.canDelete &&
    defaultShippingAddressId.value !== props.address.id &&
    defaultBillingAddressId.value !== props.address.id,
)

const setDefaultShippingAddress = async () => {
  try {
    await setDefaultCustomerShippingAddress(props.address.id)
    refreshSessionContext()
    pushSuccess(t('shop.addressCard.setDefaultShippingAddressSuccessMessage'))
  } catch {
    pushError(t('shop.addressCard.setDefaultShippingAddressErrorMessage'))
  }
}

const setDefaultBillingAddress = async () => {
  try {
    await setDefaultCustomerBillingAddress(props.address.id)
    refreshSessionContext()
    pushSuccess(t('shop.addressCard.setDefaultBillingAddressSuccessMessage'))
  } catch {
    pushError(t('shop.addressCard.setDefaultBillingAddressErrorMessage'))
  }
}

const removeAddress = async (addressId: string) => {
  try {
    await deleteCustomerAddress(addressId)
    pushSuccess(t('shop.addressCard.deleteAddressSuccessMessage'))
  } catch {
    pushError(t('shop.addressCard.deleteAddressErrorMessage'))
  }
}

const modalController = useModal()
</script>

<template>
  <div class="box">
    <div class="f-18">
      <template v-if="address.company?.trim()">{{ address.company }}<br /></template>
      <template v-if="address.department?.trim()">{{ address.department }}<br /></template>
      {{ `${address.firstName} ${address.lastName}` }}<br />
      {{ address.street }}<br />
      {{ address.zipcode }} {{ address.city }}
    </div>
    <div class="actions">
      <div v-if="canSetDefault" class="actions">
        <button
          v-if="defaultBillingAddressId !== address.id"
          :title="t('shop.addressCard.setDefaultBillingAddress')"
          @click="setDefaultBillingAddress()"
        >
          <Icon name="pricelist" />
        </button>
        <button v-else :title="t('shop.addressCard.defaultBillingAddress')" disabled>
          <Icon name="pricelist" />
        </button>
        <button
          v-if="defaultShippingAddressId !== address.id"
          :title="t('shop.addressCard.setDefaultShippingAddress')"
          @click="setDefaultShippingAddress()"
        >
          <Icon name="product" />
        </button>
        <button v-else :title="t('shop.addressCard.defaultShippingAddress')" disabled>
          <Icon name="product" />
        </button>
      </div>
      <button v-if="canEdit" :title="t('shop.addressCard.edit')" @click.prevent="modalController.open">
        <Icon name="edit" />
      </button>
      <button v-if="canBeDeleted" :title="t('shop.addressCard.delete')" @click.prevent="removeAddress(address.id)">
        <Icon name="delete" />
      </button>
    </div>
  </div>
  <ShopAddressModal
    :controller="modalController"
    :address="address"
    :countries="countries"
    :salutations="salutations"
    :salutation-loading="salutationLoading"
    :salutation-error="salutationError"
    :on-retry-salutations="onRetrySalutations"
  />
</template>
<style scoped lang="scss">
.box {
  background: var(--c-background-light);
  padding: 32px 40px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--form-spacing);
}
.actions {
  display: flex;
  justify-content: flex-end;
  svg {
    width: 20px;
    height: 20px;
    margin-left: 10px;
  }
  :deep(button) {
    color: var(--c-gray-75);
    &[disabled],
    &:hover {
      color: var(--c-primary);
    }
  }
}
</style>
